import { Trans, useLingui } from "@lingui/react/macro";
import { Heading } from "@medoma/components/Heading/Heading";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { useNavigate, useParams } from "react-router";
import { deducedError } from "@/Utils/ErrorUtils";
import { useActivity, useActivityOccurrence } from "@/api/Activities";
import type { ReactNode } from "react";
import { categorySchema } from "@medoma/models/activities";
import z from "zod";
import { EditActivityTitle } from "@/forms/EditActivity/EditActivityTitle";
import { EditActivityDescription } from "@/forms/EditActivity/EditActivityDescription";
import { EditActivityHidden } from "@/forms/EditActivity/EditActivityHidden";
import { EditActivityDoubleStaffing } from "@/forms/EditActivity/EditActivityDoubleStaffing";
import { EditActivityDuration } from "@/forms/EditActivity/EditActivityDuration";
import { EditActivityRequiredCompetences } from "@/forms/EditActivity/EditActivityRequiredCompetences";
import { EditActivityMeasurements } from "@/forms/EditActivity/EditActivityMeasurements";
import { EditActivityEndDate } from "@/forms/EditActivity/EditActivityEndDate";
import { EditActivityOccurrenceNote } from "@/forms/EditActivity/EditActivityOccurrenceNote";
import { EditActivityOccurrenceStartTime } from "@/forms/EditActivity/EditActivityOccurrenceStartTime";
import { EditActivitySchedule } from "@/forms/EditActivity/EditActivitySchedule";
import styles from "./EditActivityWrappers.module.scss";

const EditActivityScreen = ({
  title,
  children,
}: {
  title: ReactNode;
  children: ReactNode;
}) => (
  <section className={styles.editFormContainer}>
    <Heading level="h2">{title}</Heading>
    {children}
  </section>
);

const useOccurrenceParams = () =>
  z
    .object({
      activityId: z.string(),
      occurrenceId: z.string(),
    })
    .parse(useParams());

export const EditActivityTitleWrapper = () => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { activityId, occurrenceId } = useOccurrenceParams();
  const {
    data: occurrence,
    isPending,
    isError,
    error,
  } = useActivityOccurrence(activityId, occurrenceId);

  if (isPending) {
    return <Loading message={t`Hämtar aktivitet`} />;
  }
  if (isError) {
    return <ErrorMessage message={deducedError(error)} />;
  }

  return (
    <EditActivityScreen title={<Trans>Ändra titel</Trans>}>
      <EditActivityTitle
        activityId={occurrence.activityId}
        currentTitle={occurrence.title}
        onSuccess={() => navigate("..", { relative: "path" })}
      />
    </EditActivityScreen>
  );
};

export const EditActivityDescriptionWrapper = () => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { activityId, occurrenceId } = useOccurrenceParams();
  const {
    data: occurrence,
    isPending,
    isError,
    error,
  } = useActivityOccurrence(activityId, occurrenceId);

  if (isPending) {
    return <Loading message={t`Hämtar aktivitet`} />;
  }
  if (isError) {
    return <ErrorMessage message={deducedError(error)} />;
  }

  return (
    <EditActivityScreen title={<Trans>Ändra beskrivning</Trans>}>
      <EditActivityDescription
        activityId={occurrence.activityId}
        currentDescription={occurrence.description}
        onSuccess={() => navigate("..", { relative: "path" })}
      />
    </EditActivityScreen>
  );
};

export const EditActivityHiddenWrapper = () => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { activityId, occurrenceId } = useOccurrenceParams();
  const {
    data: occurrence,
    isPending,
    isError,
    error,
  } = useActivityOccurrence(activityId, occurrenceId);

  if (isPending) {
    return <Loading message={t`Hämtar aktivitet`} />;
  }
  if (isError) {
    return <ErrorMessage message={deducedError(error)} />;
  }

  return (
    <EditActivityScreen title={<Trans>Ändra dold för patienten</Trans>}>
      <EditActivityHidden
        activityId={occurrence.activityId}
        currentHidden={occurrence.hidden}
        onSuccess={() => navigate("..", { relative: "path" })}
      />
    </EditActivityScreen>
  );
};

export const EditActivityDoubleStaffingWrapper = () => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { activityId, occurrenceId } = useOccurrenceParams();
  const {
    data: occurrence,
    isPending,
    isError,
    error,
  } = useActivityOccurrence(activityId, occurrenceId);

  if (isPending) {
    return <Loading message={t`Hämtar aktivitet`} />;
  }
  if (isError) {
    return <ErrorMessage message={deducedError(error)} />;
  }

  return (
    <EditActivityScreen title={<Trans>Ändra dubbelbemanning</Trans>}>
      {occurrence.category === categorySchema.enum.HomeVisit ? (
        <EditActivityDoubleStaffing
          activityId={occurrence.activityId}
          currentDoubleStaffing={occurrence.doubleStaffing}
          onSuccess={() => navigate("..", { relative: "path" })}
        />
      ) : null}
    </EditActivityScreen>
  );
};

export const EditActivityDurationWrapper = () => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { activityId, occurrenceId } = useOccurrenceParams();
  const {
    data: occurrence,
    isPending,
    isError,
    error,
  } = useActivityOccurrence(activityId, occurrenceId);

  if (isPending) {
    return <Loading message={t`Hämtar aktivitet`} />;
  }
  if (isError) {
    return <ErrorMessage message={deducedError(error)} />;
  }

  return (
    <EditActivityScreen title={<Trans>Ändra tidsåtgång</Trans>}>
      <EditActivityDuration
        activityId={occurrence.activityId}
        currentDuration={occurrence.duration}
        onSuccess={() => navigate("..", { relative: "path" })}
      />
    </EditActivityScreen>
  );
};

export const EditActivityRequiredCompetencesWrapper = () => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { activityId, occurrenceId } = useOccurrenceParams();
  const {
    data: occurrence,
    isPending,
    isError,
    error,
  } = useActivityOccurrence(activityId, occurrenceId);

  if (isPending) {
    return <Loading message={t`Hämtar aktivitet`} />;
  }
  if (isError) {
    return <ErrorMessage message={deducedError(error)} />;
  }

  return (
    <EditActivityScreen title={<Trans>Ändra kompetensbehov</Trans>}>
      {"requiredCompetences" in occurrence ? (
        <EditActivityRequiredCompetences
          activityId={occurrence.activityId}
          currentRequiredCompetences={occurrence.requiredCompetences}
          isRequired={occurrence.category !== categorySchema.enum.AdminTask}
          onSuccess={() => navigate("..", { relative: "path" })}
        />
      ) : null}
    </EditActivityScreen>
  );
};

export const EditActivityMeasurementsWrapper = () => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { activityId, occurrenceId } = useOccurrenceParams();
  const {
    data: occurrence,
    isPending,
    isError,
    error,
  } = useActivityOccurrence(activityId, occurrenceId);

  if (isPending) {
    return <Loading message={t`Hämtar aktivitet`} />;
  }
  if (isError) {
    return <ErrorMessage message={deducedError(error)} />;
  }

  return (
    <EditActivityScreen title={<Trans>Ändra mätvärden</Trans>}>
      {occurrence.category === categorySchema.enum.PatientMeasurementTask ? (
        <EditActivityMeasurements
          activityId={occurrence.activityId}
          currentMeasurements={occurrence.measurements}
          onSuccess={() => navigate("..", { relative: "path" })}
        />
      ) : null}
    </EditActivityScreen>
  );
};

export const EditActivityScheduleWrapper = () => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { activityId } = useOccurrenceParams();
  const { data: activity, isPending, isError, error } = useActivity(activityId);

  if (isPending) {
    return <Loading message={t`Hämtar aktivitet`} />;
  }
  if (isError) {
    return <ErrorMessage message={deducedError(error)} />;
  }

  const { schedule } = activity;
  if (!("days" in schedule) && !("interval" in schedule)) {
    throw new Error(
      "EditActivityScheduleWrapper reached for a non-recurring activity",
    );
  }

  return (
    <EditActivityScreen title={<Trans>Ändra schemaläggning</Trans>}>
      <EditActivitySchedule
        activityId={activity.id}
        schedule={schedule}
        onSuccess={() => navigate("..", { relative: "path" })}
      />
    </EditActivityScreen>
  );
};

export const EditActivityOccurrenceNoteWrapper = () => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { activityId, occurrenceId } = useOccurrenceParams();
  const {
    data: occurrence,
    isPending,
    isError,
    error,
  } = useActivityOccurrence(activityId, occurrenceId);

  if (isPending) {
    return <Loading message={t`Hämtar aktivitet`} />;
  }
  if (isError) {
    return <ErrorMessage message={deducedError(error)} />;
  }

  return (
    <EditActivityScreen title={<Trans>Ändra kommentar</Trans>}>
      <EditActivityOccurrenceNote
        activityId={occurrence.activityId}
        occurrenceId={occurrence.id}
        currentNote={occurrence.note}
        onSuccess={() => navigate("..", { relative: "path" })}
      />
    </EditActivityScreen>
  );
};

export const EditActivityOccurrenceStartTimeWrapper = () => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { activityId, occurrenceId } = useOccurrenceParams();
  const {
    data: occurrence,
    isPending,
    isError,
    error,
  } = useActivityOccurrence(activityId, occurrenceId);

  if (isPending) {
    return <Loading message={t`Hämtar aktivitet`} />;
  }
  if (isError) {
    return <ErrorMessage message={deducedError(error)} />;
  }

  return (
    <EditActivityScreen title={<Trans>Ändra starttid</Trans>}>
      <EditActivityOccurrenceStartTime
        activityId={occurrence.activityId}
        occurrenceId={occurrence.id}
        currentDateTime={occurrence.start}
        currentTimeOfDay={occurrence.timeOfDay}
        currentTimespan={occurrence.timespan}
        onSuccess={() => navigate("..", { relative: "path" })}
      />
    </EditActivityScreen>
  );
};

export const EditActivityEndDateWrapper = () => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { activityId } = useOccurrenceParams();
  const { data: activity, isPending, isError, error } = useActivity(activityId);

  if (isPending) {
    return <Loading message={t`Hämtar aktivitet`} />;
  }
  if (isError) {
    return <ErrorMessage message={deducedError(error)} />;
  }

  const { schedule } = activity;
  if (!("days" in schedule) && !("interval" in schedule)) {
    throw new Error(
      "EditActivityEndDateWrapper reached for a non-recurring activity",
    );
  }

  return (
    <EditActivityScreen title={<Trans>Ändra datum</Trans>}>
      <EditActivityEndDate
        activityId={activity.id}
        schedule={schedule}
        onSuccess={() => navigate("..", { relative: "path" })}
      />
    </EditActivityScreen>
  );
};
